import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui';
import { alert } from '../utils/toast';
import type { ConfigurationExport, IntegrationRoute } from '../types';
import {
  DatabaseBackup,
  Download,
  Upload,
  FileJson,
  Check,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

export const ConfigurationManager: React.FC = () => {
  const [, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ConfigurationExport | null>(null);
  const [selectedRoutesToExport, setSelectedRoutesToExport] = useState<Set<string>>(new Set());

  const { data: currentRoutes } = useQuery({
    queryKey: ['integration-routes'],
    queryFn: async () => {
      try {
        const response = await api.get<{ routes: IntegrationRoute[] }>('/admin/integration-routes');
        return response.data.routes || [];
      } catch {
        return [];
      }
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const queryParams =
        selectedRoutesToExport.size > 0
          ? `?route_ids=${Array.from(selectedRoutesToExport).join(',')}`
          : '';
      const response = await api.get<ConfigurationExport>(
        `/admin/configuration/export${queryParams}`
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `integration-config-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      void alert.success('Exportado!', 'Configuração exportada com sucesso!');
    },
  });

  const importMutation = useMutation({
    mutationFn: async (config: ConfigurationExport) => {
      const response = await api.post('/admin/configuration/import', config);
      return response.data;
    },
    onSuccess: () => {
      setFile(null);
      setPreviewData(null);
      void alert.success('Importado!', 'Configuração importada com sucesso!');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        try {
          const parsed = JSON.parse(content);
          setPreviewData(parsed);
        } catch {
          void alert.error('Erro', 'Formato JSON inválido no ficheiro');
          setPreviewData(null);
        }
      };
      reader.readAsText(selectedFile);
    } else {
      setFile(null);
      setPreviewData(null);
    }
  };

  const handleImport = async () => {
    if (!previewData) return;

    const result = await alert.confirm(
      'Importar Configuração?',
      'Deseja importar esta configuração? Isto irá criar novas rotas e mapeamentos de integração.'
    );

    if (result.isConfirmed) {
      importMutation.mutate(previewData);
    }
  };

  const calculateDiff = () => {
    if (!previewData || !currentRoutes) return [];

    return previewData.routes.map((importedRoute) => {
      const existing = currentRoutes.find(
        (r) =>
          r.endpoint_path === importedRoute.endpoint_path &&
          r.http_method === importedRoute.http_method
      );
      if (!existing) return { route: importedRoute, status: 'NOVO' as const };

      if (
        existing.version !== importedRoute.version ||
        existing.external_url !== importedRoute.external_url
      ) {
        return { route: importedRoute, status: 'MODIFICADO' as const };
      }
      return { route: importedRoute, status: 'INALTERADO' as const };
    });
  };

  const routeDiffs = calculateDiff();

  const getMethodBadgeClass = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800';
      case 'POST':
        return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'PUT':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800';
      case 'DELETE':
        return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-rose-200 dark:border-rose-800';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Backup e Migração</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center">
          <DatabaseBackup className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">
            Backup e Migração
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Exporte e importe configurações de integração, rotas e mapeamentos entre ambientes.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Download className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Exportar Configuração
              </h2>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
              Exporte todas as rotas (ou uma seleção específica) como um ficheiro JSON estruturado
              para efeitos de salvaguarda ou migração para outros ambientes.
            </p>

            {currentRoutes && currentRoutes.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Rotas Disponíveis
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedRoutesToExport(new Set(currentRoutes.map((r) => r.id)))
                      }
                      className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Selecionar Todas
                    </button>
                    <span className="text-slate-300 dark:text-slate-700">|</span>
                    <button
                      type="button"
                      onClick={() => setSelectedRoutesToExport(new Set())}
                      className="text-xs font-semibold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                    >
                      Limpar
                    </button>
                  </div>
                </div>

                <div className="max-h-56 overflow-y-auto border border-slate-200 dark:border-slate-750 rounded-xl p-2.5 space-y-1.5 bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-750/50">
                  {currentRoutes.map((route) => (
                    <label
                      key={route.id}
                      className="flex items-center space-x-2.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700/60 p-2 rounded-lg cursor-pointer transition-colors pt-2 first:pt-1"
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoutesToExport.has(route.id)}
                        onChange={(e) => {
                          const newSet = new Set(selectedRoutesToExport);
                          if (e.target.checked) newSet.add(route.id);
                          else newSet.delete(route.id);
                          setSelectedRoutesToExport(newSet);
                        }}
                        className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${getMethodBadgeClass(route.http_method)}`}
                      >
                        {route.http_method}
                      </span>
                      <span className="font-mono font-semibold text-slate-900 dark:text-white">
                        {route.endpoint_path}
                      </span>
                      <span className="text-slate-400 truncate text-[11px]">- {route.name}</span>
                    </label>
                  ))}
                </div>

                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 font-medium">
                  {selectedRoutesToExport.size === 0
                    ? 'Todas as rotas registadas serão exportadas.'
                    : `${selectedRoutesToExport.size} de ${currentRoutes.length} rotas selecionadas.`}
                </p>
              </div>
            )}
          </div>

          <div>
            {exportMutation.isError && (
              <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 p-3.5">
                <p className="text-xs font-medium text-rose-800 dark:text-rose-300">
                  {getErrorMessage(exportMutation.error)}
                </p>
              </div>
            )}

            <button
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
              className="btn-primary w-full py-2.5 text-xs font-bold flex items-center justify-center gap-2 shadow-sm"
            >
              {exportMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> A exportar configuração...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" /> Descarregar Ficheiro JSON
                </>
              )}
            </button>
          </div>
        </div>

        {/* Import */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Upload className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Importar Configuração
              </h2>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
              Carregue um ficheiro JSON de configuração para restaurar ou replicar rotas e
              transformações.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                  Ficheiro de Configuração (.json)
                </label>
                <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-4 text-center hover:border-primary-400 dark:hover:border-primary-500 transition-colors bg-slate-50/50 dark:bg-slate-800/40">
                  <FileJson className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <input
                    type="file"
                    id="import-file"
                    accept=".json,application/json"
                    className="block w-full text-xs text-slate-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary-50 dark:file:bg-primary-900/40 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 cursor-pointer"
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              {previewData && (
                <div className="bg-primary-50/60 dark:bg-primary-950/30 p-4 rounded-xl border border-primary-200/80 dark:border-primary-900/60 animate-fade-in">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-xs text-primary-900 dark:text-primary-300 flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-primary-600" /> Pré-visualização do
                      Ficheiro
                    </h3>
                    <span className="text-[10px] font-mono bg-white dark:bg-slate-800 px-2 py-0.5 rounded border border-primary-200 dark:border-primary-800 text-slate-600 dark:text-slate-300">
                      v{previewData.version}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-400 mb-3">
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-400 block">
                        Exportado em
                      </span>
                      <span>{new Date(previewData.exported_at).toLocaleDateString('pt-PT')}</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-400 block">
                        Total de Rotas
                      </span>
                      <span className="font-bold text-slate-900 dark:text-white">
                        {previewData.routes.length} rotas
                      </span>
                    </div>
                  </div>

                  {routeDiffs.length > 0 && (
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1.5">
                        Diferenças Detetadas
                      </span>
                      <div className="max-h-44 overflow-y-auto bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-slate-50 dark:bg-slate-800/80 text-[10px] uppercase text-slate-400 font-bold border-b border-slate-100 dark:border-slate-800">
                            <tr>
                              <th className="px-3 py-1.5">Ação</th>
                              <th className="px-3 py-1.5">Método</th>
                              <th className="px-3 py-1.5">Caminho</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono text-[11px]">
                            {routeDiffs.map((diff, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-1.5">
                                  {diff.status === 'NOVO' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                      <Check className="w-2.5 h-2.5" /> Novo
                                    </span>
                                  )}
                                  {diff.status === 'MODIFICADO' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                      <RefreshCw className="w-2.5 h-2.5" /> Modificado
                                    </span>
                                  )}
                                  {diff.status === 'INALTERADO' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                                      Inalterado
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 font-bold">
                                  <span
                                    className={`px-1.5 py-0.5 rounded text-[10px] border ${getMethodBadgeClass(diff.route.http_method)}`}
                                  >
                                    {diff.route.http_method}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-slate-900 dark:text-slate-200 truncate max-w-[140px]">
                                  {diff.route.endpoint_path}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="pt-4">
            {importMutation.isError && (
              <div className="mb-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 p-3.5 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                <p className="text-xs font-medium text-rose-800 dark:text-rose-300">
                  {getErrorMessage(importMutation.error)}
                </p>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={!previewData || importMutation.isPending}
              className="btn-primary w-full py-2.5 text-xs font-bold flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> A importar configuração...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" /> Importar Configuração
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
